import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ROUTES } from '@shared/constants'
import { authService } from '../services/authService'
import LoadingSpinner from '../components/LoadingSpinner'
import ParticlesBackground from '../components/ParticlesBackground'
import { RequirementItem } from '../components/RequirementItem'


/**
 * Handles password reset completion with token validation.
 * Validates new password against security requirements in real-time.
 */

export default function ResetPasswordPage() {

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [submissionAttempted, setSubmissionAttempted] = useState(false)
  const [validating, setValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  
  const token = searchParams.get('token')

  // Real-time password validation for better UX
  const passwordRequirements = useMemo(() => ({
    minLength: newPassword.length >= 8,
    hasMixedCase: /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecial: /[^A-Za-z0-9]/.test(newPassword),
    passwordsMatch: newPassword.length > 0 && newPassword === confirmPassword
  }), [newPassword, confirmPassword])


  /**
   * Validates reset token on mount before allowing password change.
   * Prevents expired or reused tokens from being accepted.
   */

  useEffect(() => {

    const validateToken = async () => {

      if (!token) {
        setStatus('error')
        setMessage('Invalid or missing reset token')
        setValidating(false)
        return
      }

      try {
        // Validate token server-side
        const data = await authService.validateResetToken(token)
        
        if (data.valid) {
          setTokenValid(true)
        } else {
          setStatus('error')
          setMessage(data.message || 'This password reset link has expired or has already been used')
        }
      } catch (error) {
        setStatus('error')
        setMessage('Unable to validate reset link. Please try again or request a new one.')
      } finally {
        setValidating(false)
      }
    }

    validateToken()
  }, [token])


  /**
   * Updates password after validating requirements and token.
   * Redirects to login on success for immediate sign-in.
   */

  const handleSubmit = async (e: React.FormEvent) => {

    e.preventDefault()
    setSubmissionAttempted(true)
    
    // Validate password match
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match')
      return
    }

    // Validate minimum length
    if (newPassword.length < 8) {
      setMessage('Password must be at least 8 characters')
      return
    }

    // Validate token presence
    if (!token) {
      setMessage('Invalid reset token')
      return
    }

    setStatus('submitting')
    setMessage('')

    try {
      const data = await authService.resetPassword(token, newPassword)

      if (data.success) {
        setStatus('success')
        setMessage('Password reset successfully! Redirecting to login...')
        setTimeout(() => {
          navigate(ROUTES.LOGIN, { replace: true })
        }, 2000)
      } else {
        setStatus('error')
        setMessage(data.message || 'Password reset failed')
      }
    } catch (error) {
      setStatus('error')
      setMessage(error instanceof Error ? error.message : 'An error occurred. Please try again.')
    }
  }


  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 px-4 relative overflow-hidden">
        <ParticlesBackground />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-600 p-8 ring-1 ring-slate-500/20 max-w-md w-full text-center relative z-10"
        >
          <LoadingSpinner size="large" />
          <p className="text-gray-300 mt-4">Validating reset link...</p>
        </motion.div>
      </div>
    )
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 px-4 relative overflow-hidden">
        <ParticlesBackground />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-600 p-8 ring-1 ring-slate-500/20 max-w-md w-full text-center relative z-10"
        >
          <div className="w-16 h-16 bg-red-500/20 border border-red-500/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Link Expired</h1>
          <p className="text-gray-300 mb-6">{message || 'This password reset link has expired or has already been used. Please request a new one.'}</p>
          <button
            onClick={() => navigate(ROUTES.LOGIN)}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white py-3 rounded-lg font-medium hover:from-blue-500 hover:to-blue-400 transition shadow-lg shadow-blue-500/30"
          >
            Go to Login
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 px-4 relative overflow-hidden">
      <ParticlesBackground />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900/50 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-600 p-8 ring-1 ring-slate-500/20 max-w-md w-full relative z-10"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Reset Password
          </h1>
          <p className="text-gray-300">
            Create a new secure password for your account
          </p>
        </div>

        {status === 'success' ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500/20 border border-green-500/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-400 font-medium">{message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-200 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 pr-12 border border-slate-600 bg-slate-800/60 text-white placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="At least 8 characters"
                  disabled={status === 'submitting'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition"
                  tabIndex={-1}
                  disabled={status === 'submitting'}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
              
              {/* Password requirements checklist */}
              <div className="mt-3 space-y-2">
                <RequirementItem met={passwordRequirements.minLength}>
                  8 or more characters
                </RequirementItem>
                <RequirementItem met={passwordRequirements.hasMixedCase}>
                  Uppercase and lowercase
                </RequirementItem>
                <RequirementItem met={passwordRequirements.hasNumber}>
                  Number (0-9)
                </RequirementItem>
                <RequirementItem met={passwordRequirements.hasSpecial}>
                  Special character (!@#$%^&*)
                </RequirementItem>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-200 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-12 border border-slate-600 bg-slate-800/60 text-white placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Re-enter your password"
                  disabled={status === 'submitting'}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition"
                  tabIndex={-1}
                  disabled={status === 'submitting'}
                >
                  {showConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
              
              {/* Password match indicator */}
              {confirmPassword.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 text-sm">
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                      passwordRequirements.passwordsMatch 
                        ? 'bg-green-100' 
                        : (submissionAttempted ? 'bg-red-100' : 'bg-gray-100')
                    }`}>
                      {passwordRequirements.passwordsMatch ? (
                        <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className={`w-3 h-3 ${
                          submissionAttempted ? 'text-red-600' : 'text-gray-400'
                        }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                    <span className={
                      passwordRequirements.passwordsMatch 
                        ? 'text-green-400 font-medium' 
                        : (submissionAttempted ? 'text-red-400 font-medium' : 'text-gray-300')
                    }>
                      {passwordRequirements.passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {message && status === 'error' && (
              <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                <p className="text-sm text-red-300">{message}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white py-3 rounded-lg font-medium hover:from-blue-500 hover:to-blue-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg shadow-blue-500/30"
            >
              {status === 'submitting' ? (
                <>
                  <LoadingSpinner size="small" />
                  <span className="ml-2">Resetting Password...</span>
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  )
}
